import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useBreadcrumbs } from "contexts/BreadcrumbsContext";
import { format } from "date-fns";
import { DataTable } from "components/ui/data-table";
import { createColumns, InStockShipment } from "./columns";
import { getCustomerCodes, getBranches, CustomerOption, BranchOption, getInStockShipments, getInStockLocations } from "../api/shipments";

interface PaginationState {
    pageIndex: number;
    pageSize: number;
}

interface SortingState {
    id: string;
    desc: boolean;
}

type DropdownOption = {
  label: string;
  value: string;
};

const InStockShipmentsPage: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();
    const [searchParams, setSearchParams] = useSearchParams();

    const [shipments, setShipments] = useState<InStockShipment[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");

    const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
    const [branchOptions, setBranchOptions] = useState<BranchOption[]>([]);

    const [totalCount, setTotalCount] = useState(0);
    const [pageCount, setPageCount] = useState(0);

    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: parseInt(searchParams.get("page") || "1") - 1,
        pageSize: parseInt(searchParams.get("per_page") || "20"),
    });

    const [sorting, setSorting] = useState<SortingState[]>([
        {
            id: searchParams.get("sort_by") || "cn_date",
            desc: searchParams.get("sort_order") === "desc",
        },
    ]);

    const [globalFilter, setGlobalFilter] = useState(searchParams.get("search") || "");
    const [selectedCustId, setSelectedCustId] = useState<string | undefined>(searchParams.get("customer") || undefined);

    const [selectedOuCode, setSelectedOuCode] = useState<string | undefined>(searchParams.get("branch") || undefined);

    const [selectedStockStatus, setSelectedStockStatus] = useState<string | undefined>(searchParams.get("stock_status") || undefined);
    const [selectedStockLocation, setSelectedStockLocation] = useState<string | undefined>(searchParams.get("stock_location") || undefined);

    const [stockLocationOptions, setStockLocationOptions] = useState<{ label: string; value: string }[]>([]);
    const stockStatusOptions = [
        { label: "On Hold", value: "on_hold" },
        { label: "Awaiting Pickup", value: "awaiting_pickup" },
    ];

    useEffect(() => {
        setBreadcrumbs([
            { label: "Shipments", href: "#" },
            { label: "In Stock" },
        ]);
    }, [setBreadcrumbs]);

    useEffect(() => {
        const next = new URLSearchParams();

        next.set("page", String(pagination.pageIndex + 1));
        next.set("per_page", String(pagination.pageSize));

        if (sorting.length) {
            next.set("sort_by", sorting[0].id);
            next.set("sort_order", sorting[0].desc ? "desc" : "asc");
        }

        if (globalFilter) next.set("search", globalFilter);
        if (selectedCustId) next.set("customer", selectedCustId);
        if (selectedOuCode) next.set("branch", selectedOuCode);

        if (selectedStockStatus) next.set("stock_status", selectedStockStatus);
        if (selectedStockLocation) next.set("stock_location", selectedStockLocation);

        setSearchParams(next, { replace: true });
    }, [pagination, sorting, globalFilter, selectedCustId, selectedOuCode, selectedStockStatus, selectedStockLocation, setSearchParams]);


    const fetchCustomerData = useCallback(async () => {
        try {
            const params: any = {};
            if (globalFilter) params.search = globalFilter;

            const response = await getCustomerCodes(params);

            if (response?.length) {
                const options = response
                    .filter((c: any) => c?.cust_code)
                    .map((c: any) => ({
                        cust_code: c.cust_code,
                        cust_name: c.cust_name || c.cust_code,
                    }))
                    .sort((a, b) => a.cust_name.localeCompare(b.cust_name));

                setCustomerOptions(options);
            } else {
                setCustomerOptions([]);
            }
        } catch (error) {
            console.error("Customer fetch failed", error);
            setCustomerOptions([]);
        }
    }, [globalFilter]);


    const fetchBranchData = useCallback(async () => {
        try {
            const params: any = {};
            if (globalFilter) params.search = globalFilter;

            const branches = await getBranches(params);
            setBranchOptions(branches.sort((a, b) => a.branch_name.localeCompare(b.branch_name)));
        } catch (error) {
            console.error("Branch fetch failed", error);
            setBranchOptions([]);
        }
    }, [globalFilter]);

    const fetchStockLocations = useCallback(async () => {
        try {
            const locations = await getInStockLocations(selectedCustId);
            if (locations?.length) {
                const options = locations
                    .filter((l: any) => l?.branch_ou_code)
                    .map((l: any) => ({
                        value: l.branch_ou_code,
                        label: l.branch_name || l.branch_ou_code
                    }))
                    .sort((a: DropdownOption, b: DropdownOption) =>
                        a.label.localeCompare(b.label)
                    );

                setStockLocationOptions(options);
            } else {
                setStockLocationOptions([]);
            }
        } catch (error) {
            console.error("Stock location fetch failed", error);
            setStockLocationOptions([]);
        }
    }, [selectedCustId]);

    const fetchShipments = useCallback(async () => {
        setLoading(true);
        try {
            const params: any = {
                page: pagination.pageIndex + 1,
                per_page: pagination.pageSize,
                search: globalFilter,
                sort_by: sorting[0]?.id,
                sort_order: sorting[0]?.desc ? "desc" : "asc",
            };

            if (selectedCustId) params.cust_id = selectedCustId;
            if (selectedOuCode) params.booking_branch = selectedOuCode;

            if (selectedStockStatus) params.stock_status = selectedStockStatus;
            if (selectedStockLocation) params.stock_location = selectedStockLocation;

            const response = await getInStockShipments(params);

            setShipments(response.items || []);
            setTotalCount(response.total_items || 0);
            setPageCount(response.total_pages || 0);
            setError("");
        } catch (error: any) {
            console.error("Fetch shipments failed", error);
            setShipments([]);
            setTotalCount(0);
            setPageCount(0);
            setError(error?.response?.data?.detail || error?.message || "Failed to fetch shipments");
        } finally {
            setLoading(false);
        }
    }, [pagination, globalFilter, sorting, selectedCustId, selectedOuCode, selectedStockStatus, selectedStockLocation]);

    useEffect(() => {
        fetchShipments();
    }, [fetchShipments]);

    useEffect(() => {
        fetchCustomerData();
    }, [fetchCustomerData]);

    useEffect(() => {
        fetchBranchData();
    }, [fetchBranchData]);

    useEffect(() => {
        fetchStockLocations();
    }, [fetchStockLocations]);

    const columns = createColumns();

    return (
        <div className="h-full flex flex-col overflow-hidden">
            <div className="flex-1 min-h-0">
                <DataTable
                    columns={columns}
                    data={shipments}
                    loading={loading}
                    error={error}
                    pagination={pagination}
                    onPaginationChange={setPagination}
                    sorting={sorting}
                    onSortingChange={(val) => {
                        setSorting(val);
                        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                    globalFilter={globalFilter}
                    onGlobalFilterChange={setGlobalFilter}
                    totalCount={totalCount}
                    pageCount={pageCount}
                    customerOptions={customerOptions}
                    selectedCustId={selectedCustId}
                    onCustomerChange={(code) => {
                        setSelectedCustId(code);
                        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                    branchOptions={branchOptions}
                    selectedOuCode={selectedOuCode}
                    onBranchChange={(code) => {
                        setSelectedOuCode(code);
                        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                    stockStatusOptions={stockStatusOptions}
                    selectedStockStatus={selectedStockStatus}
                    onStockStatusChange={(val) => {
                        setSelectedStockStatus(val);
                        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                    stockLocationOptions={stockLocationOptions}
                    selectedStockLocation={selectedStockLocation}
                    onStockLocationChange={(val) => {
                        setSelectedStockLocation(val);
                        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                    }}
                    isLoadingAlerts={false}
                />
            </div>
        </div>
    );
};

export default InStockShipmentsPage;